import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import LordIcon from './LordIcon';
import LORDICONS from '../lordicons';
import {
  acceptAllCookies,
  acceptNecessaryCookies,
  COOKIE_CONSENT_OPEN_EVENT,
  hasCookieConsent,
  readCookieConsent,
  saveCookiePreferences,
} from '../utils/cookieConsent';

const CHARACTER_SRC = '/home/cookie-consent-character.png?v=2';

const CookieConsent: React.FC = () => {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(true);

  useEffect(() => {
    const open = () => {
      const current = readCookieConsent();
      setAnalytics(current?.analytics ?? true);
      setShowPrefs(false);
      setVisible(true);
    };

    if (!hasCookieConsent()) open();

    const onOpenEvent = () => open();

    const onDocClick = (event: MouseEvent) => {
      const target = event.target as Element | null;
      const link = target?.closest?.('a[href="#cookies"]');
      if (!link) return;
      event.preventDefault();
      open();
    };

    const onHash = () => {
      if (window.location.hash === '#cookies') open();
    };

    window.addEventListener(COOKIE_CONSENT_OPEN_EVENT, onOpenEvent);
    document.addEventListener('click', onDocClick);
    window.addEventListener('hashchange', onHash);
    onHash();

    return () => {
      window.removeEventListener(COOKIE_CONSENT_OPEN_EVENT, onOpenEvent);
      document.removeEventListener('click', onDocClick);
      window.removeEventListener('hashchange', onHash);
    };
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    setVisible(false);
    setShowPrefs(false);
  };

  const handleAccept = () => {
    acceptAllCookies();
    dismiss();
  };

  const handleNecessary = () => {
    acceptNecessaryCookies();
    dismiss();
  };

  const handleSavePrefs = () => {
    saveCookiePreferences(analytics);
    dismiss();
  };

  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[120] flex justify-center overflow-visible p-4 sm:justify-start sm:p-6"
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-body"
    >
      <div className="pointer-events-auto relative w-full max-w-[552px] overflow-visible pt-0 min-[551px]:pt-28 sm:max-w-[672px] sm:pt-40">
        <div
          className="relative flex items-stretch gap-0 overflow-visible rounded-[22px] bg-white pl-5 pr-5 shadow-[0_12px_40px_rgba(15,23,42,0.14)] min-[551px]:gap-8 min-[551px]:pl-8 min-[551px]:pr-16 sm:gap-12 sm:pl-12 sm:pr-20"
          style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
        >
          <button
            type="button"
            onClick={handleNecessary}
            className="absolute right-3 top-3 z-20 flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 sm:right-4 sm:top-4"
            aria-label={t('cookies.close')}
          >
            <LordIcon
              src={LORDICONS.cross}
              trigger="hover"
              size={18}
              color="#1b1b2f"
              hoverColor="#0065F4"
              target="button"
            />
          </button>

          <div className="relative z-[1] min-w-0 flex-1 py-6 sm:py-7">
            <h2
              id="cookie-consent-title"
              className="text-[28px] font-bold leading-none tracking-tight text-[#1b1b2f] sm:text-[32px]"
            >
              {t('cookies.title')}
            </h2>

            {!showPrefs ? (
              <>
                <p
                  id="cookie-consent-body"
                  className="mt-3 text-[14px] leading-relaxed text-[#4B5563] sm:text-[15px]"
                >
                  {t('cookies.body')}{' '}
                  <a
                    href="#cookies"
                    className="font-semibold text-[#1b1b2f] underline decoration-[#1b1b2f]/40 underline-offset-2 hover:text-[#0065F4] hover:decoration-[#0065F4]"
                  >
                    {t('cookies.policy')}
                  </a>
                  .
                </p>

                <div className="mt-5 flex flex-wrap gap-2.5">
                  <button
                    type="button"
                    onClick={handleAccept}
                    className="rounded-[10px] bg-[#1b1b2f] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#2a2a45]"
                  >
                    {t('cookies.accept')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPrefs(true)}
                    className="rounded-[10px] bg-[#e9eaed] px-5 py-2.5 text-sm font-semibold text-[#1b1b2f] transition-colors hover:bg-[#dde0e5]"
                  >
                    {t('cookies.preferences')}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p
                  id="cookie-consent-body"
                  className="mt-3 text-[14px] leading-relaxed text-[#4B5563] sm:text-[15px]"
                >
                  {t('cookies.prefs.intro')}
                </p>

                <div className="mt-4 space-y-3">
                  <label className="flex items-start gap-3 rounded-xl border border-gray-100 bg-gray-50 px-3.5 py-3">
                    <input
                      type="checkbox"
                      checked
                      disabled
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600"
                    />
                    <span>
                      <span className="block text-sm font-semibold text-[#1b1b2f]">
                        {t('cookies.prefs.necessary')}
                      </span>
                      <span className="mt-0.5 block text-xs leading-relaxed text-gray-500">
                        {t('cookies.prefs.necessary.desc')}
                      </span>
                    </span>
                  </label>

                  <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-100 bg-white px-3.5 py-3 transition-colors hover:border-gray-200">
                    <input
                      type="checkbox"
                      checked={analytics}
                      onChange={(e) => setAnalytics(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>
                      <span className="block text-sm font-semibold text-[#1b1b2f]">
                        {t('cookies.prefs.optional')}
                      </span>
                      <span className="mt-0.5 block text-xs leading-relaxed text-gray-500">
                        {t('cookies.prefs.optional.desc')}
                      </span>
                    </span>
                  </label>
                </div>

                <div className="mt-5 flex flex-wrap gap-2.5">
                  <button
                    type="button"
                    onClick={handleSavePrefs}
                    className="rounded-[10px] bg-[#1b1b2f] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#2a2a45]"
                  >
                    {t('cookies.prefs.save')}
                  </button>
                  <button
                    type="button"
                    onClick={handleAccept}
                    className="rounded-[10px] bg-[#e9eaed] px-5 py-2.5 text-sm font-semibold text-[#1b1b2f] transition-colors hover:bg-[#dde0e5]"
                  >
                    {t('cookies.accept')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPrefs(false)}
                    className="rounded-[10px] px-3 py-2.5 text-sm font-medium text-gray-500 transition-colors hover:text-[#1b1b2f]"
                  >
                    {t('cookies.prefs.back')}
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="relative hidden w-[120px] shrink-0 self-stretch min-[551px]:block sm:w-[148px]" aria-hidden="true">
            <img
              src={CHARACTER_SRC}
              alt=""
              className="pointer-events-none absolute bottom-0 left-1/2 z-10 h-[calc(100%+5.5rem)] w-auto max-w-none -translate-x-1/2 select-none object-contain object-bottom sm:h-[calc(100%+7rem)]"
              draggable={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
